/* eslint-disable indent */
/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');
const libChipTooltipsCSS = require('./PictProvider-ChipTooltips-CSS.js');

/**
 * ChipTooltips — runtime behavior for the "chips" that the LabelWithChips
 * MetaTemplate renders next to form-field labels.
 *
 * The MetaTemplate emits each chip as a `<button class="chip" data-chip-for
 * data-chip-label ...>` with a zero-width `.chip-label` span. This provider gives
 * the chip its two states:
 *   - UNPINNED: hover / focus shows the chip's value in a portaled pict-section-
 *     modal tooltip (no DOM reflow); and
 *   - PINNED: clicking a chip toggles its whole *type* (e.g. "New",
 *     "ExternalDatabaseID") active and persists it via the existing
 *     HeadlightPaneSettings.toggleChipTypeActive (ActiveChipTypes). That toggles
 *     an `is-active` class on every chip of the type, and the headlight-styles
 *     CSS expands the `.chip-label` inline — the value reads as an extension of
 *     the field label, in the content flow. A pinned chip shows no portaled
 *     tooltip, so pinned chips never overlay the labels/inputs above them.
 *
 * Graceful fallback: if the Pict-Section-Modal view is not registered (or is an
 * older build without richTooltip), the chip falls back to a native `title`
 * tooltip — no hard dependency, nothing breaks. The inline pinned state is
 * independent of the modal (it's pure CSS + the settings view).
 *
 * Wiring is reactive: a MutationObserver re-sweeps as forms re-render (tab
 * switches, recordset row add/remove, dynamic sections), wiring any new chip
 * once and destroying handles for chips that leave the DOM.
 */
class ChipTooltips extends PictProvider
{
	/**
	 * @param {import('fable')} pFable
	 * @param {Record<string, any>} [pOptions]
	 * @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this._started = false;
		this._sweepPending = false;
		this._observer = null;
		this._cssInjected = false;
		// Per-type hover-tooltip registry: { [chipTypeId]: Array<{ element, handle }> }.
		// `handle` is null for a pinned chip (it shows its value inline, no tooltip).
		this._handlesByType = {};
	}

	/**
	 * Begin wiring chips. Idempotent — safe to call from every host app's
	 * attachInstructions(). No-op outside a browser (tests / SSR).
	 */
	start()
	{
		if (this._started || typeof document === 'undefined')
		{
			return;
		}
		this._started = true;
		this._injectChipCSS();
		// Wire whatever is already on screen, after the current render flushes.
		this._scheduleSweep();
		// Re-wire as forms re-render. MutationObserver is the sanctioned way to
		// react to DOM the framework rebuilds outside our own render cycle.
		if (typeof MutationObserver !== 'undefined' && document.body)
		{
			this._observer = new MutationObserver(() => this._scheduleSweep());
			this._observer.observe(document.body, { childList: true, subtree: true });
		}
	}

	/**
	 * Register + inject the chip CSS once (the chip color/structure styling
	 * lives in the headlight-styles Bulma layer; this is the small set of
	 * component-owned rules — the button-anchor a11y ring and tooltip body).
	 */
	_injectChipCSS()
	{
		if (this._cssInjected || !this.pict || !this.pict.CSSMap || typeof this.pict.CSSMap.addCSS !== 'function')
		{
			return;
		}
		this.pict.CSSMap.addCSS('Headlight-ChipTooltips', libChipTooltipsCSS, 500);
		if (typeof this.pict.CSSMap.injectCSS === 'function')
		{
			this.pict.CSSMap.injectCSS();
		}
		this._cssInjected = true;
	}

	/** Coalesce many mutations into a single wiring pass per tick. */
	_scheduleSweep()
	{
		if (this._sweepPending)
		{
			return;
		}
		this._sweepPending = true;
		setTimeout(() =>
		{
			this._sweepPending = false;
			this.wireChips();
		}, 0);
	}

	/** The modal view, if registered and tooltip-capable; otherwise null. */
	get modal()
	{
		const tmpModal = this.pict && this.pict.views && this.pict.views['Pict-Section-Modal'];
		return (tmpModal && typeof tmpModal.richTooltip === 'function') ? tmpModal : null;
	}

	/** The settings view that owns the persisted ActiveChipTypes preference. */
	get paneSettings()
	{
		return (this.pict && this.pict.views && this.pict.views.HeadlightPaneSettings) || null;
	}

	/**
	 * Whether a chip type is currently "active" (pinned — shows its label inline).
	 *
	 * @param {string} pId
	 * @return {boolean}
	 */
	checkChipTypeActive(pId)
	{
		const tmpView = this.paneSettings;
		return !!(tmpView && typeof tmpView.checkChipTypeActive === 'function' && tmpView.checkChipTypeActive(pId));
	}

	/** Wire every not-yet-wired chip currently in the DOM. */
	wireChips()
	{
		if (typeof document === 'undefined')
		{
			return;
		}
		this._pruneDetachedHandles();
		const tmpChips = document.querySelectorAll('.chip[data-chip-for]:not([data-chip-wired])');
		for (let i = 0; i < tmpChips.length; i++)
		{
			this._wireChip(/** @type {HTMLElement} */ (tmpChips[i]));
		}
	}

	/**
	 * Wire a single chip: seed its pinned (is-active) state, attach the click
	 * that toggles the whole type, and give it a hover tooltip while unpinned.
	 *
	 * @param {HTMLElement} pChip
	 */
	_wireChip(pChip)
	{
		pChip.setAttribute('data-chip-wired', 'true');
		const tmpId = pChip.getAttribute('data-chip-for') || '';
		const tmpLabel = pChip.getAttribute('data-chip-label') || '';

		const tmpModal = this.modal;
		if (!tmpModal)
		{
			// Graceful fallback: native tooltip, no reflow, no dependency. (The
			// inline pinned state still works — it's CSS + the settings view.)
			if (tmpLabel)
			{
				pChip.setAttribute('title', tmpLabel);
			}
			return;
		}

		// Seed the persisted pinned state onto chips rendered after load (tab
		// switches, dynamic rows) so the inline label shows immediately; chips
		// present at load already got is-active from the settings view.
		const tmpActive = this.checkChipTypeActive(tmpId);
		if (tmpActive)
		{
			pChip.classList.add('is-active');
		}

		// Click toggles the whole chip TYPE's pinned state + persists it. A real
		// listener, not an inline onclick in the emitted markup.
		pChip.addEventListener('click', (pEvent) => this._onChipToggle(pEvent, tmpId));

		// Hover/focus tooltip for the UNPINNED state only — a pinned chip shows
		// its value inline, so it needs no portaled tooltip.
		const tmpHandle = tmpActive ? null : this._attachHoverTooltip(pChip);

		if (!this._handlesByType[tmpId])
		{
			this._handlesByType[tmpId] = [];
		}
		this._handlesByType[tmpId].push({ element: pChip, handle: tmpHandle });
	}

	/**
	 * Attach a portaled hover/focus tooltip (pict-section-modal richTooltip) to a
	 * chip and return its handle (or null if the modal is unavailable).
	 *
	 * @param {HTMLElement} pChip
	 * @return {?{ destroy: function }}
	 */
	_attachHoverTooltip(pChip)
	{
		const tmpModal = this.modal;
		if (!tmpModal)
		{
			return null;
		}
		const tmpLabel = pChip.getAttribute('data-chip-label') || '';
		// Theme the tooltip bubble to the chip's color variant (e.g. is-info ->
		// chip-tooltip-info); unknown/default variants keep the standard dark
		// tooltip. The chip-tooltip-bubble marker scopes component CSS (e.g. the
		// z-index below the Select2 dropdown) to chip tooltips only. See
		// PictProvider-ChipTooltips-CSS.
		const tmpVariant = (pChip.getAttribute('data-chip-color') || '').replace(/^is-/, '').trim();
		const tmpClassName = 'chip-tooltip-bubble' + (tmpVariant ? ` chip-tooltip-${tmpVariant}` : '');
		return tmpModal.richTooltip(pChip, this._buildTooltipHTML(pChip, tmpLabel),
			{
				position: 'top',
				className: tmpClassName,
			});
	}

	/**
	 * Build the tooltip body for a chip — the icon for brand consistency plus
	 * the chip's text.
	 *
	 * @param {HTMLElement} pChip
	 * @param {string} pLabel
	 * @return {string}
	 */
	_buildTooltipHTML(pChip, pLabel)
	{
		const tmpIcon = pChip.getAttribute('data-chip-icon') || 'fa-info';
		const fEscape = (pStr) => String(pStr == null ? '' : pStr)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		return `<span class="chip-tooltip"><i class="fa fa-fw ${fEscape(tmpIcon)}"></i> ${fEscape(pLabel)}</span>`;
	}

	/**
	 * Toggle a chip TYPE's pinned state on click. Delegates the state change to
	 * HeadlightPaneSettings (flips ActiveChipTypes, toggles the `is-active` class
	 * on every chip of the type so the CSS expands the inline label, and persists
	 * to the server), then reconciles the hover tooltips for that type.
	 *
	 * @param {Event} pEvent
	 * @param {string} pId
	 */
	_onChipToggle(pEvent, pId)
	{
		const tmpView = this.paneSettings;
		if (!tmpView || typeof tmpView.toggleChipTypeActive !== 'function')
		{
			return;
		}
		tmpView.toggleChipTypeActive(pEvent, pId);
		this._syncHoverTooltips(pId);
	}

	/**
	 * Keep the hover tooltips consistent with the pinned state: a pinned chip
	 * shows its value inline and gets no hover tooltip; an unpinned one does.
	 *
	 * @param {string} pId
	 */
	_syncHoverTooltips(pId)
	{
		const tmpActive = this.checkChipTypeActive(pId);
		const tmpList = this._handlesByType[pId] || [];
		for (let i = 0; i < tmpList.length; i++)
		{
			const tmpEntry = tmpList[i];
			if (tmpActive && tmpEntry.handle)
			{
				// Now pinned: drop the portaled hover tooltip (value is inline).
				if (typeof tmpEntry.handle.destroy === 'function')
				{
					tmpEntry.handle.destroy();
				}
				tmpEntry.handle = null;
			}
			else if (!tmpActive && !tmpEntry.handle && tmpEntry.element && tmpEntry.element.isConnected)
			{
				// Now unpinned: (re)attach the hover tooltip.
				tmpEntry.handle = this._attachHoverTooltip(tmpEntry.element);
			}
		}
	}

	/**
	 * Drop handles whose chip element has left the DOM (form re-rendered),
	 * destroying their tooltips so no orphaned tooltip lingers.
	 */
	_pruneDetachedHandles()
	{
		const tmpKeys = Object.keys(this._handlesByType);
		for (let k = 0; k < tmpKeys.length; k++)
		{
			const tmpList = this._handlesByType[tmpKeys[k]];
			const tmpKept = [];
			for (let i = 0; i < tmpList.length; i++)
			{
				const tmpEntry = tmpList[i];
				if (tmpEntry.element && tmpEntry.element.isConnected)
				{
					tmpKept.push(tmpEntry);
				}
				else if (tmpEntry.handle && typeof tmpEntry.handle.destroy === 'function')
				{
					tmpEntry.handle.destroy();
				}
			}
			if (tmpKept.length)
			{
				this._handlesByType[tmpKeys[k]] = tmpKept;
			}
			else
			{
				delete this._handlesByType[tmpKeys[k]];
			}
		}
	}
}

module.exports = ChipTooltips;
